import React, { useEffect, useState } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader, Card, CardHeader,
  Table, Thead, Th, Td, TRow,
  Button, StatCard, TabGroup,
  FormGroup, Input, Select, Muted,
} from "../ui";

interface ValuationProduct {
  id: string;
  name: string;
  brand: string;
  category: string;
  oz_on_hand: number;
  oz_per_case: number;
  current_cost_per_case: number;
  valuation: number;
  cases_on_hand: number;
}

interface Transaction {
  id: string;
  product_name: string;
  oz_delta: number;
  transaction_type: string;
  note: string;
  created_by_name: string;
  created_at: string;
}

const TYPE_COLORS: Record<string, string> = {
  receive: "var(--rbw-success)",
  sale: "var(--rbw-danger)",
  waste: "#D97706",
  count_adjustment: "var(--rbw-blue-dark)",
  transfer: "#7C3AED",
};

const TABS = [
  { key: "valuation", label: "Valuation" },
  { key: "transactions", label: "Transactions" },
  { key: "adjust", label: "Adjust" },
];

export default function InventoryView() {
  const [tab, setTab] = useState("valuation");
  const [valuation, setValuation] = useState<{ products: ValuationProduct[]; total_valuation: number } | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [adjustProduct, setAdjustProduct] = useState("");
  const [adjustOz, setAdjustOz] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [products, setProducts] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (tab === "valuation") {
      api.get("/inventory/valuation").then((r) => setValuation(r.data));
    } else if (tab === "transactions") {
      api.get("/inventory/transactions?limit=100").then((r) => setTransactions(r.data));
    } else {
      api.get("/products?is_active=true").then((r) => setProducts(r.data));
    }
  }, [tab]);

  const submitAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post("/inventory/adjust", {
      product_id: adjustProduct,
      new_oz_on_hand: Number(adjustOz),
      note: adjustNote,
    });
    setAdjustProduct("");
    setAdjustOz("");
    setAdjustNote("");
    alert("Inventory adjusted");
  };

  return (
    <PageContainer>
      <PageHeader title="Inventory" subtitle="Track stock levels and adjustments" />

      <TabGroup tabs={TABS} active={tab} onChange={setTab} />

      {tab === "valuation" && valuation && (
        <>
          <div className="grid grid-cols-1 mb-6">
            <StatCard
              label="Total Inventory Valuation"
              value={`$${Number(valuation.total_valuation).toFixed(2)}`}
              variant="green"
            />
          </div>
          <Table>
            <Thead>
              <Th>Product</Th>
              <Th align="right">Cases</Th>
              <Th align="right">Ounces</Th>
              <Th align="right">Cost/Case</Th>
              <Th align="right">Value</Th>
            </Thead>
            <tbody>
              {valuation.products.map((p, idx) => (
                <TRow key={p.id} index={idx}>
                  <Td>
                    <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>{p.name}</div>
                    <Muted>{p.category}</Muted>
                  </Td>
                  <Td align="right">{Number(p.cases_on_hand).toFixed(1)}</Td>
                  <Td align="right">{Number(p.oz_on_hand).toFixed(0)}</Td>
                  <Td align="right">${Number(p.current_cost_per_case).toFixed(2)}</Td>
                  <Td align="right" bold>${Number(p.valuation).toFixed(2)}</Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {tab === "transactions" && (
        <Table>
          <Thead>
            <Th>Date</Th>
            <Th>Product</Th>
            <Th>Type</Th>
            <Th align="right">Oz Change</Th>
            <Th>Note</Th>
            <Th>By</Th>
          </Thead>
          <tbody>
            {transactions.map((tx, idx) => (
              <TRow key={tx.id} index={idx}>
                <Td>{new Date(tx.created_at).toLocaleString()}</Td>
                <Td bold>{tx.product_name}</Td>
                <Td>
                  <span className="text-xs font-semibold" style={{ color: TYPE_COLORS[tx.transaction_type] || "var(--rbw-gray-dark)" }}>
                    {tx.transaction_type}
                  </span>
                </Td>
                <Td align="right">
                  <span className="font-medium" style={{ color: Number(tx.oz_delta) >= 0 ? "var(--rbw-success)" : "var(--rbw-danger)" }}>
                    {Number(tx.oz_delta) >= 0 ? "+" : ""}{Number(tx.oz_delta).toFixed(1)}
                  </span>
                </Td>
                <Td>{tx.note || "-"}</Td>
                <Td>{tx.created_by_name || "-"}</Td>
              </TRow>
            ))}
          </tbody>
        </Table>
      )}

      {tab === "adjust" && (
        <Card className="max-w-lg">
          <CardHeader title="Inventory Adjustment" />
          <form onSubmit={submitAdjustment} className="space-y-4">
            <FormGroup label="Product">
              <Select value={adjustProduct} onChange={setAdjustProduct}>
                <option value="">Select product...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="New Oz On Hand">
              <Input type="number" step="0.01" value={adjustOz} onChange={setAdjustOz} required />
            </FormGroup>
            <FormGroup label="Reason">
              <Input value={adjustNote} onChange={setAdjustNote} required placeholder="Physical count adjustment" />
            </FormGroup>
            <Button type="submit">Submit Adjustment</Button>
          </form>
        </Card>
      )}
    </PageContainer>
  );
}
